import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BuyTokens } from '../components/BuyTokens';
import { AllGenerationsViewer } from '../components/AllGenerationsViewer';
import { OnboardingTour } from '../components/OnboardingTour';

interface DashboardProps {
  onNavigate: (
    page:
      | 'dashboard'
      | 'moodboard'
      | 'your-gallery'
      | 'ai-touchup'
      | 'ai-cleanup'
      | 'ai-remove-furniture'
      | 'admin'
      | 'settings'
  ) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [showBuyTokens, setShowBuyTokens] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const tokenBalance = useMemo(() => user?.tokens || 0, [user?.tokens]);

  // Check if user is new (you can customize this logic)
  const isNewUser = useMemo(() => {
    if (!user) return false;
    // Consider a user "new" if they have the default token amount and no stored preference
    const hasSeenOnboarding = localStorage.getItem(`onboarding-seen-${user.id}`);
    return !hasSeenOnboarding && tokenBalance <= 50; // Default starting tokens
  }, [user, tokenBalance]);

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    if (user?.id) {
      localStorage.setItem(`onboarding-seen-${user.id}`, 'true');
    }
  };

  const generationCards = [
    {
      key: 'moodboard' as const,
      title: 'Moodboard',
      emoji: '🎨',
      description:
        'Mix inspirations, styles, and colors to create multiple property design ideas in seconds.',
      images: ['/login-images/ai-moodboard-1754994620677-c7xwamy70xrma0crkqabajw878.webp', '/login-images/ai-moodboard-1754994620677-yzrragp70drma0crkqaama9c9r.webp'],
    },
    {
      key: 'ai-touchup' as const,
      title: 'Edit',
      emoji: '🖌️',
      description:
        'Quickly mask any area to erase, change, or clean with AI handles the perfect finish.',
      images: ['/login-images/Screenshot_-_2025-08-12T122407.061.png', '/login-images/ai-erase-1754994539986.png'],
    },
    {
      key: 'ai-cleanup' as const,
      title: 'Restyle',
      emoji: '🚀',
      description:
        'Show any room in multiple styles to help clients imagine its full potential.',
      images: ['/login-images/ai-upstaging-1754993294796-optimized.jpg', '/login-images/ai-erase-1754993394027.png'],
    },
    {
      key: 'ai-remove-furniture' as const,
      title: 'Remove Furniture',
      emoji: '🪑',
      description:
        'Instantly clear furniture from any room to showcase its true, open space.',
      images: ['/login-images/1758104640579-5404s3.jpg', '/login-images/ai-furniture-removal-1758546484649.jpg'],
    },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="md:col-span-2 lg:col-span-2">
            <div className="dashboard-welcome bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Welcome to RenovirAI!
              </h2>
              <p className="text-gray-600 mb-4">
              Save time on listing photos and convert more buyers by showcasing each property’s true potential with AI.
              </p>
              <p className="text-sm text-gray-500 mb-4">
              Choose a plan and start transforming your listings today! 
              </p>
              <div className="flex space-x-4">
                
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="relative bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  🎯 {isNewUser ? 'Take the Tour' : 'Start Tour'}
                  {isNewUser && (
                    <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      New!
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Generation Types */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Explore features</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {generationCards.map((card) => (
                  <button
                    key={card.key}
                    type="button"
                    onClick={() => onNavigate(card.key)}
                    className={`generation-card-${card.key} w-full text-left border rounded-lg p-4 hover:shadow transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {card.images.map((src, idx) => (
                        <img
                          key={idx}
                          src={src}
                          alt={`${card.title} example ${idx + 1}`}
                          className="h-24 w-full object-cover rounded"
                          loading="lazy"
                        />
                      ))}
                    </div>
                    <div className="mt-3 font-semibold text-gray-900">{card.title}</div>
                    <p className="mt-1 text-sm text-gray-600">{card.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="all-generations-viewer">
              <AllGenerationsViewer />
            </div>

          </div>
          <div className="space-y-6 md:col-span-1 lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Your Account</h3>
              <dl className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <dt>Email:</dt>
                  <dd className="font-medium text-gray-900">{user?.email}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>User ID:</dt>
                  <dd className="font-mono text-xs bg-gray-100 p-1 rounded">{user?.id}</dd>
                </div>
                <div className="token-balance flex justify-between items-center">
                  <dt>Tokens:</dt>
                  <dd className="text-lg font-bold text-blue-600">{tokenBalance}</dd>
                </div>
              </dl>
            </div>
            
            {/* Removed debug utilities */}

          </div>
        </div>
      </div>

      {showBuyTokens && (
        <BuyTokens onClose={() => setShowBuyTokens(false)} />
      )}

      <OnboardingTour
        isRunning={showOnboarding}
        onClose={handleOnboardingClose}
        autoStart={isNewUser}
      />
    </div>
  );
}; 